import {EventsSummary, useEventsSummary} from '@domain/shifthandover'
import {DataContentWrapper} from '@hconnect/common/containers'
import {CardBox, SummaryScores, type SummaryScoreProps} from '@hconnect/uikit/src/lib2'
import {spawnErrorToast} from '@hm/fundamental'
import {Box, SxProps, Theme, Typography} from '@mui/material'
import {TFunction} from 'i18next'
import {Moment} from 'moment-timezone'
import {useCallback} from 'react'
import {useTranslation} from 'react-i18next'

import {EVENTS_SUMMARY_WIDGET_NAMESPACE} from '../consts'

type Props = {
  plantId: string
  from?: Moment
  to?: Moment
  cardBoxSx?: SxProps<Theme>
  placeholderWrapperSx?: SxProps<Theme>
}

const getSummaryScoreItems = (t: TFunction, data?: EventsSummary): SummaryScoreProps[] => {
  return data
    ? [
        {
          label: t('eventsSummary.label.general'),
          value: data.generalCount,
          valueVariant: 'neutral',
          showTooltip: false,
          dataTestId: 'events-general'
        },
        {
          label: t('eventsSummary.label.production'),
          value: data.productionCount,
          valueVariant: 'neutral',
          showTooltip: false,
          dataTestId: 'events-production'
        },
        {
          label: t('eventsSummary.label.maintenance'),
          value: data.maintenanceCount,
          valueVariant: 'neutral',
          showTooltip: false,
          dataTestId: 'events-maintenance'
        }
      ]
    : []
}

export const EventsSummaryContainer: React.FC<Props> = ({
  plantId,
  from,
  to,
  cardBoxSx,
  placeholderWrapperSx
}) => {
  const {t} = useTranslation(EVENTS_SUMMARY_WIDGET_NAMESPACE)

  const notifyFailedToFetchEvents = useCallback(
    () => spawnErrorToast(t('eventsSummary.message.failedToFetchEvents')),
    [t]
  )

  const isTimeRangeSupported = !!from && !!to
  const {data, isLoading, isError, isRefetching, refetch} = useEventsSummary(
    {
      plantId,
      startDate: from?.toISOString(),
      endDate: to?.toISOString()
    },
    {enabled: isTimeRangeSupported, onError: notifyFailedToFetchEvents}
  )

  const items = getSummaryScoreItems(t, data)

  return (
    <CardBox sx={cardBoxSx} data-test-id="events-summary-card">
      <Box display="flex" flexDirection="row">
        <Typography variant="h3" mb={2}>
          {t('eventsSummary.cardTitle')}
        </Typography>
      </Box>
      <DataContentWrapper
        showLoader={isLoading}
        isError={isError}
        isRefetching={isRefetching}
        data={data}
        retryFunction={() => void refetch()}
        progressSx={{color: 'primary.main'}}
        placeholderWrapperSx={placeholderWrapperSx}
        noDataSettings={{
          ...(!isTimeRangeSupported && {
            noDataMessage: t('eventsSummary.message.unsupportedTimeRange')
          })
        }}
      >
        <SummaryScores items={items} />
      </DataContentWrapper>
    </CardBox>
  )
}
